"use client"

import '../../mockups/_styles/editorial.css'
import { PressArticleCard } from '@/components/press/press-article-card'
import type { PressCardImageData } from '@/lib/press-card-image'

import { SiteHeader } from "@/components/site-header"
import { SiteFooter } from "@/components/site-footer"
import { TRUST_SIGNALS } from "@/lib/trust-signals"
import Link from "next/link"
import {
  Quote,
  BarChart3,
  Calculator,
  Rss,
  Database,
  Newspaper,
  BookOpen,
  Users,
  Wrench,
} from "lucide-react"
import {
  ArrowRightIcon,
  CalendarIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExternalLinkIcon,
  GlobeIcon,
  ShoppingBagIcon,
} from "@/components/brand-kit/icon-sprite"
import { ProductShareButtons } from "@/components/blocks/product-share-buttons"
import { CitationKit } from "@/components/data/citation-kit"
import { GooglePreferredButton } from "@/components/chrome/google-preferred-button"
import { CTABand } from "@/components/brand-kit/cta-band"
import { Breadcrumb } from "@/components/brand-kit/breadcrumb"
import { pressReleaseBreadcrumbs } from "@/lib/breadcrumbs"
import type { ParsedPressContent } from "@/lib/press-content-parser"

interface PressRelease {
  id: number
  slug: string
  title: string
  summary: string | null
  published_at: string
  ein_url: string | null
  image: string | null
  topics: string[] | null
}

interface Enrichment {
  keyQuotes?: string[]
  relatedTools?: { name: string; url: string; benefit: string }[]
  relatedCollections?: { name: string; url: string }[]
  keyFindings?: string[]
  dataSources?: string[]
  /** FRIDGE-607 — the standing rule: 15 page-specific Q&As per release. */
  faqs?: { question: string; answer: string }[]
}

interface RelatedPR {
  cardImage?: PressCardImageData | null
  slug: string
  title: string
  summary: string | null
  published_at: string
  image: string | null
  topics: string[] | null
}

interface AdjacentPR {
  slug: string
  title: string
}

interface PressReleaseClientProps {
  pressRelease: PressRelease
  parsedContent: ParsedPressContent
  enrichment: Enrichment | null
  relatedReleases: RelatedPR[]
  adjacent: { next: AdjacentPR | null; prev: AdjacentPR | null }
}

/**
 * The publication date, formatted in UTC — never in the reader's timezone.
 *
 * `published_at` is stored at midnight UTC for most releases, so formatting it
 * in local time renders the PREVIOUS day for every visitor west of Greenwich:
 * the server (Vercel, UTC) emitted "March 24, 2026" and a New York browser
 * re-rendered "March 23, 2026". React reports that as a hydration mismatch
 * (#418) and throws away the server HTML for the subtree. FRIDGE-382.
 *
 * UTC is also the correct answer on the merits: this is the date the release
 * went out, it is what the dateline inside the body text says, and it is what
 * `<time dateTime={published_at}>` and the JSON-LD `datePublished` carry.
 */
function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

export default function PressReleaseClient({
  pressRelease,
  parsedContent,
  enrichment,
  relatedReleases,
  adjacent,
}: PressReleaseClientProps) {
  return (
    <div className="ed-root min-h-screen bg-white">
      <SiteHeader />

      <main id="main-content">
        {/* HERO */}
        <section style={{ borderBottom: '1px solid #f3f4f6' }}>
          <div className="ed-container" style={{ paddingTop: 36, paddingBottom: 36 }}>
            {/* ONE trail (lib/breadcrumbs.ts) — page.tsx emits the same
                pressReleaseBreadcrumbs(pressRelease) as the BreadcrumbList
                (FRIDGE-269). The leaf carries the FULL title so it equals the
                schema name; the CSS ellipsis (`truncate`) keeps it to one line
                where the old code cut the text at 60 characters. */}
            <Breadcrumb
              variant="editorial"
              trail={pressReleaseBreadcrumbs(pressRelease)}
              style={{ marginBottom: 24 }}
              currentClassName="leaf truncate max-w-full"
            />

            <div className="ed-eyebrow" style={{ marginBottom: 18 }}>
              <span className="dot" />
              PRESS RELEASE · {formatDate(pressRelease.published_at).toUpperCase()}
            </div>

            <h1 className="ed-h1" style={{ fontSize: 'clamp(34px, 4.2vw, 58px)' }}>
              {pressRelease.title}
            </h1>

            {pressRelease.summary && pressRelease.summary !== pressRelease.title && (
              <p className="ed-subtitle" style={{ maxWidth: 760, fontSize: 17, marginTop: 12 }}>
                {pressRelease.summary}
              </p>
            )}

            {pressRelease.topics && pressRelease.topics.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-6">
                {pressRelease.topics.map((topic) => (
                  <span
                    key={topic}
                    className="px-3 py-1 bg-[#f2f7ff] text-[#0469f0] text-sm font-medium rounded-full capitalize"
                  >
                    {topic}
                  </span>
                ))}
              </div>
            )}

            <div style={{ marginTop: 18 }}>
              <ProductShareButtons productTitle={pressRelease.title} productImage={parsedContent.images[0]?.src} pageType="press" variant="top" />
            </div>
          </div>
        </section>

        {/* DATA SOURCES STRIP */}
        {enrichment?.dataSources && enrichment.dataSources.length > 0 && (
          <section style={{ borderBottom: '1px solid #f3f4f6', background: 'var(--gray-50)' }}>
            <div className="ed-container" style={{ paddingTop: 16, paddingBottom: 16 }}>
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <Database className="w-4 h-4 text-[#ff9951] shrink-0" />
                <span className="font-semibold text-gray-900">Data sources:</span>
                <span className="text-gray-700">{enrichment.dataSources.join(', ')}</span>
                {/* FRIDGE-336: site-first releases publish here before the wire —
                    the wire link renders only once ein_url exists, never fabricated. */}
                {pressRelease.ein_url && (
                  <>
                    <span className="text-gray-300">·</span>
                    <a
                      href={pressRelease.ein_url}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="text-[#0469f0] hover:underline inline-flex items-center gap-1"
                    >
                      View on EIN Presswire
                      <ExternalLinkIcon className="w-3 h-3" />
                    </a>
                  </>
                )}
              </div>
            </div>
          </section>
        )}

        {/* LEAD QUOTE — one pull-quote, not a stack (owner 08/28: the old
            every-quote block with its "— Press Team, Fridge.com" footers
            "looks weird and wrong"). The first key quote is the release's
            hook; the rest already live in the body below. Attribution names
            the report, not a faceless team. */}
        {enrichment?.keyQuotes && enrichment.keyQuotes.length > 0 && (
          <section style={{ borderBottom: '1px solid #f3f4f6' }}>
            <div className="ed-container" style={{ paddingTop: 32, paddingBottom: 32 }}>
              <div className="max-w-3xl mx-auto">
                <div className="relative">
                  <Quote className="w-10 h-10 text-[#ff9951]/30 absolute -top-2 -left-2" />
                  <blockquote className="pl-8 border-l-4 border-[#ff9951]">
                    <p className="text-xl md:text-2xl font-medium text-gray-900 leading-relaxed italic">
                      &ldquo;{enrichment.keyQuotes[0]}&rdquo;
                    </p>
                    <footer className="mt-2 text-sm text-gray-500">
                      — from the Fridge.com report
                    </footer>
                  </blockquote>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* MAIN CONTENT + SIDEBAR */}
        <section style={{ borderBottom: '1px solid #f3f4f6' }}>
          <div className="ed-container" style={{ paddingTop: 40, paddingBottom: 40 }}>
            <div className="lg:grid lg:grid-cols-[1fr_320px] lg:gap-10">

              {/* MAIN CONTENT */}
              <div className="min-w-0">
                {/* KEY FINDINGS */}
                {enrichment?.keyFindings && enrichment.keyFindings.length > 0 && (
                  <div className="mb-8 bg-white border border-gray-200 rounded-xl p-5">
                    <span className="font-mono text-[11px] font-bold tracking-[0.15em] text-[#ff9951] block mb-3">
                      KEY FINDINGS
                    </span>
                    <div className="flex items-center gap-2 mb-4">
                      <BarChart3 className="w-5 h-5 text-[#0469f0]" />
                      <h2 className="text-lg font-bold text-gray-900">Highlights from the report</h2>
                    </div>
                    <ul className="space-y-3">
                      {enrichment.keyFindings.map((finding, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <span className="mt-1 w-5 h-5 rounded-full bg-[#0469f0] text-white text-xs font-bold flex items-center justify-center shrink-0">
                            {i + 1}
                          </span>
                          <span className="text-sm text-gray-700 leading-relaxed">{finding}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* ARTICLE BODY — first image floats right, body wraps around it */}
                <style
                  dangerouslySetInnerHTML={{
                    __html: `
                      .pr-body { font-size: 17px; line-height: 1.7; color: #374151; }
                      .pr-body > p,
                      .pr-body > ul,
                      .pr-body > ol { margin: 0 0 22px; }
                      .pr-body > p:first-of-type {
                        font-size: 20px;
                        line-height: 1.55;
                        color: #111827;
                        font-style: italic;
                        font-weight: 500;
                        border-left: 4px solid #ff9951;
                        padding-left: 18px;
                        margin: 4px 0 28px;
                      }
                      .pr-body > p:first-of-type i { font-style: inherit; }
                      .pr-body h2 {
                        font-size: 28px;
                        font-weight: 800;
                        letter-spacing: -0.02em;
                        color: #111827;
                        margin: 40px 0 14px;
                        line-height: 1.2;
                        clear: both;
                      }
                      .pr-body h3 {
                        font-size: 22px;
                        font-weight: 800;
                        letter-spacing: -0.01em;
                        color: #111827;
                        margin: 36px 0 12px;
                        line-height: 1.25;
                        padding-top: 4px;
                        clear: both;
                      }
                      .pr-body h4 {
                        font-size: 17px;
                        font-weight: 700;
                        color: #111827;
                        margin: 24px 0 8px;
                      }
                      .pr-body a {
                        color: #0469f0;
                        text-decoration: underline;
                        text-decoration-color: rgba(4, 105, 240, 0.3);
                        text-underline-offset: 3px;
                      }
                      .pr-body a:hover { text-decoration-color: #0469f0; }
                      .pr-body strong { color: #111827; font-weight: 700; }
                      .pr-body ul, .pr-body ol { padding-left: 0; list-style: none; }
                      .pr-body li {
                        position: relative;
                        padding-left: 24px;
                        margin-bottom: 10px;
                        line-height: 1.65;
                      }
                      .pr-body ul li::before {
                        content: "";
                        position: absolute;
                        left: 6px;
                        top: 0.7em;
                        width: 6px;
                        height: 6px;
                        border-radius: 50%;
                        background: #ff9951;
                      }
                      .pr-body ol { counter-reset: pr-list; }
                      .pr-body ol li { counter-increment: pr-list; }
                      .pr-body ol li::before {
                        content: counter(pr-list, decimal-leading-zero);
                        position: absolute;
                        left: 0;
                        top: 0;
                        font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
                        font-size: 12px;
                        font-weight: 700;
                        color: #ff9951;
                        letter-spacing: 0.05em;
                      }
                      .pr-body blockquote {
                        border-left: 4px solid #ff9951;
                        padding: 4px 0 4px 18px;
                        margin: 22px 0;
                        font-style: italic;
                        color: #111827;
                      }
                      /* Wire dateline — parser step 9.4 wraps it; rendered as
                         a muted small-caps line above the release copy. */
                      .pr-body .pr-dateline {
                        display: block;
                        font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
                        font-size: 11.5px;
                        font-weight: 700;
                        letter-spacing: 0.08em;
                        text-transform: uppercase;
                        color: #6e7788; /* FRIDGE-1504 L1504-5: 4.51:1 on white; #9ca3af gave 2.53 */
                        margin-bottom: 10px;
                      }
                      .pr-body .pr-dateline a { color: inherit; text-decoration: none; }
                      /* Data-row tables lifted out of wall-of-text runs by
                         press-content-parser step 9.5 — state/city rows with
                         rate + FIS chips. */
                      .pr-body .pr-datarows {
                        margin: 0 0 26px;
                        border: 1px solid #e5e7eb;
                        border-radius: 12px;
                        overflow: hidden;
                        background: #fff;
                        clear: both;
                      }
                      /* FRIDGE-1488: rows carry no per-row classes (a 50-state
                         release holds 200 of them); each row part is styled here,
                         once per document. Row: name, chip, then the insight on
                         its own line. */
                      .pr-body .pr-datarows > div {
                        display: flex;
                        flex-wrap: wrap;
                        align-items: center;
                        gap: 2px 10px;
                        padding: 12px 16px;
                        border-top: 1px solid #f1f3f6;
                      }
                      .pr-body .pr-datarows > div:first-child { border-top: 0; }
                      .pr-body .pr-datarows > div > strong { font-weight: 800; color: #111827; font-size: 15.5px; }
                      .pr-body .pr-datarows > div > span {
                        font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
                        font-size: 11.5px;
                        font-weight: 700;
                        color: #b45309;
                        background: rgba(255, 153, 81, 0.12);
                        border: 1px solid rgba(255, 153, 81, 0.35);
                        padding: 2px 8px;
                        border-radius: 999px;
                        white-space: nowrap;
                      }
                      .pr-body .pr-datarows > div > span.fis {
                        color: #0353b8;
                        background: rgba(4, 105, 240, 0.07);
                        border-color: rgba(4, 105, 240, 0.25);
                      }
                      .pr-body .pr-datarows > div > span.pop {
                        color: #4b5563;
                        background: #f3f4f6;
                        border-color: #e5e7eb;
                      }
                      .pr-body .pr-datarows > div > p {
                        flex-basis: 100%;
                        margin: 0;
                        font-size: 14.5px;
                        line-height: 1.6;
                        color: #4b5563;
                      }
                      .pr-body .pr-datarows > .sub { padding-left: 36px; background: #fafbfc; position: relative; }
                      .pr-body .pr-datarows > .sub::before {
                        content: "";
                        position: absolute;
                        left: 18px;
                        top: 19px;
                        width: 7px;
                        height: 7px;
                        border-radius: 50%;
                        background: rgba(4, 105, 240, 0.35);
                      }
                      .pr-body .pr-datarows > .note {
                        display: block;
                        padding: 12px 16px;
                        font-size: 14.5px;
                        line-height: 1.6;
                        color: #4b5563;
                        background: #f9fafb;
                        border-top: 1px solid #f1f3f6;
                      }
                      /* Lead image: float right so the body text wraps around it. */
                      .pr-figure-lead {
                        float: right;
                        width: 320px;
                        max-width: 45%;
                        margin: 4px 0 18px 28px;
                        border-radius: 8px;
                        overflow: hidden;
                        border: 1px solid #e5e7eb;
                        background: #fff;
                        display: block;
                      }
                      .pr-figure-lead img { display: block; width: 100%; height: auto; }
                      .pr-figure-lead .pr-figure-open,
                      .pr-image-grid .pr-figure-open {
                        display: block;
                        font-size: 12px;
                        line-height: 1.4;
                        color: #0469f0;
                        padding: 6px 12px 10px;
                        text-decoration: underline;
                      }
                      .pr-figure-lead figcaption {
                        font-size: 12px;
                        color: #6b7280;
                        padding: 8px 12px;
                        background: #f9fafb;
                        line-height: 1.45;
                      }
                      @media (max-width: 640px) {
                        .pr-figure-lead {
                          float: none;
                          width: 100%;
                          max-width: 100%;
                          margin: 4px 0 22px;
                        }
                      }
                      /* Additional images grid below the body. */
                      .pr-image-grid {
                        clear: both;
                        margin-top: 32px;
                        display: grid;
                        gap: 12px;
                        grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
                      }
                      .pr-image-grid a {
                        display: block;
                        border-radius: 8px;
                        overflow: hidden;
                        border: 1px solid #e5e7eb;
                        background: #fff;
                      }
                      .pr-image-grid img { width: 100%; height: auto; display: block; }
                      .pr-image-grid figcaption {
                        font-size: 12px;
                        color: #6b7280;
                        padding: 8px 12px;
                        background: #f9fafb;
                      }
                    `,
                  }}
                />
                <article className="pr-body">
                  {parsedContent.images.length > 0 && (
                    <a
                      href={parsedContent.images[0].largeSrc}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="pr-figure-lead"
                      aria-label={parsedContent.images[0].alt || 'Press release image'}
                    >
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src={parsedContent.images[0].src}
                        alt={parsedContent.images[0].alt}
                        loading="eager"
                      />
                      {parsedContent.images[0].caption && (
                        <figcaption>{parsedContent.images[0].caption}</figcaption>
                      )}
                      {/* FRIDGE-1333: an image-only link has no anchor text for a
                          crawler; this visible line names where the link goes. */}
                      <span className="pr-figure-open">View full-size image</span>
                    </a>
                  )}
                  <div dangerouslySetInnerHTML={{ __html: parsedContent.bodyHtml }} />
                  {parsedContent.images.length > 1 && (
                    <div className="pr-image-grid">
                      {parsedContent.images.slice(1).map((img, i) => (
                        <a
                          key={i}
                          href={img.largeSrc}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={img.alt || 'Press release image'}
                        >
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          <img src={img.src} alt={img.alt} loading="lazy" />
                          {img.caption && <figcaption>{img.caption}</figcaption>}
                          <span className="pr-figure-open">View full-size image</span>
                        </a>
                      ))}
                    </div>
                  )}
                </article>

                {/* FAQ — FRIDGE-607: the 15 grounded questions each release
                    carries in enrichment.faqs. Visible for readers, full text
                    in the DOM for AI answer engines; PressReleaseSchemas emits
                    the matching FAQPage JSON-LD from the same rows. */}
                {enrichment?.faqs && enrichment.faqs.length > 0 && (
                  <section aria-labelledby="pr-faq-heading" className="mt-12">
                    <span className="font-mono text-[11px] font-bold tracking-[0.15em] text-[#ff9951] block mb-2">
                      COMMON QUESTIONS
                    </span>
                    <h2 id="pr-faq-heading" className="text-2xl font-black text-gray-900 mb-5">
                      Frequently asked questions
                    </h2>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden divide-y divide-gray-100">
                      {enrichment.faqs.map((faq, i) => (
                        <details key={i} className="group" open={i === 0}>
                          <summary className="flex items-start justify-between gap-4 cursor-pointer list-none px-5 py-4 hover:bg-gray-50 [&::-webkit-details-marker]:hidden">
                            <span className="font-semibold text-[15px] text-gray-900 leading-snug">
                              {faq.question}
                            </span>
                            {/* gray-500, not gray-400: the ratchet may only
                                FALL, and this chevron is perfectly legible at
                                4.84:1 — there was no reason for it to be fainter. */}
                            <ChevronDownIcon className="w-4 h-4 mt-1 shrink-0 text-gray-500 transition-transform group-open:rotate-180" />
                          </summary>
                          <p className="px-5 pb-4 text-[14.5px] leading-relaxed text-gray-600">
                            {faq.answer}
                          </p>
                        </details>
                      ))}
                    </div>
                  </section>
                )}

                {/* ATTRIBUTION — wire credit only when the wire actually ran (FRIDGE-336) */}
                <div className="mt-10 pt-6 border-t border-gray-200">
                  <p className="text-sm text-gray-500">
                    {pressRelease.ein_url ? (
                      <>
                        Originally distributed via{' '}
                        <a
                          href={pressRelease.ein_url}
                          target="_blank"
                          rel="nofollow noopener noreferrer"
                          className="text-[#0469f0] hover:underline inline-flex items-center gap-1"
                        >
                          EIN Presswire
                          <ExternalLinkIcon className="w-3 h-3" />
                        </a>
                        {' '}on {formatDate(pressRelease.published_at)}
                      </>
                    ) : (
                      <>Published by Fridge.com Press on {formatDate(pressRelease.published_at)}</>
                    )}
                  </p>
                </div>

                {/* SHARE */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <ProductShareButtons productTitle={pressRelease.title} productImage={parsedContent.images[0]?.src} pageType="press" variant="bottom" />
                </div>

                {/* CITATION KIT — item 197 Phase 2: the same journalist kit the
                    /data pages carry, on every press release. */}
                <CitationKit
                  datasetName={pressRelease.title}
                  pageUrl={`https://fridge.com/press/${pressRelease.slug}`}
                  // getUTCFullYear, not getFullYear: the same timezone trap as
                  // formatDate above — a release published 00:00 UTC on Jan 1
                  // would cite the previous year in every western browser and
                  // mismatch the server HTML.
                  year={String(new Date(pressRelease.published_at).getUTCFullYear())}
                  docKind="page"
                  heading="Cite this release"
                />

                {/* FRIDGE-608 — the big Google button at the release's foot,
                    INSIDE the content column, centered — the reference site's
                    article-bottom placement. (It briefly sat after this
                    column's closing tag, which made it a stray grid item in
                    the 320px sidebar track — the owner called it, rightly:
                    ridiculous.) */}
                <GooglePreferredButton center className="mt-8" />
              </div>

              {/* FRIDGE-609 — the narrow-screen ABOUT placement: right after
                  the release content, not at the page's bottom. Hidden on
                  desktop where the sidebar instance renders. */}
              <div className="lg:hidden mt-10">
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                  {/* FRIDGE-609 step-up: an orange accent band anchors the block
                      as the page's identity card instead of a gray sidebar tile. */}
                  <div className="h-1 bg-[#ff9951]" aria-hidden="true" />
                  <div className="p-5">
                  <span className="font-mono text-[11px] font-bold tracking-[0.15em] text-[#ff9951] block mb-3">
                    ABOUT FRIDGE.COM
                  </span>
                  <div className="flex items-center gap-2 mb-4">
                    <GlobeIcon className="w-5 h-5 text-[#0469f0]" />
                    <h3 className="font-black text-gray-900 text-[15px]">The refrigerator and freezer search engine</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                    The authoritative refrigerator and freezer search engine in the United States.
                  </p>
                  {/* The owner's four ruled trust lines (lib/trust-signals.ts) —
                      the same set the footer and Why-trust band render. */}
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    {TRUST_SIGNALS.map((signal) => (
                      <div key={signal.key} className="text-center p-2 bg-gray-50 rounded-lg">
                        <div className="text-sm font-bold text-gray-900">{signal.value}</div>
                        <div className="text-xs text-gray-500 leading-snug">{signal.label}</div>
                      </div>
                    ))}
                  </div>
                  <div className="space-y-2">
                    <Link href="/tools" className="flex items-center gap-2 text-sm text-[#0469f0] hover:underline">
                      <Wrench className="w-3.5 h-3.5" /> Explore free tools
                    </Link>
                    <Link href="/collections/refrigerators" className="flex items-center gap-2 text-sm text-[#0469f0] hover:underline">
                      <ShoppingBagIcon className="w-3.5 h-3.5" /> Shop refrigerators
                    </Link>
                    <Link href="/blogs/news" className="flex items-center gap-2 text-sm text-[#0469f0] hover:underline">
                      <BookOpen className="w-3.5 h-3.5" /> Read expert articles
                    </Link>
                  </div>
                  </div>
                </div>
              </div>

              {/* SIDEBAR */}
              <aside className="mt-10 lg:mt-0 space-y-4">
                {/* RELATED TOOLS */}
                {enrichment?.relatedTools && enrichment.relatedTools.length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-xl p-5">
                    <span className="font-mono text-[11px] font-bold tracking-[0.15em] text-[#ff9951] block mb-3">
                      FREE TOOLS
                    </span>
                    <div className="flex items-center gap-2 mb-4">
                      <Calculator className="w-5 h-5 text-[#0469f0]" />
                      <h3 className="font-bold text-gray-900">Calculators</h3>
                    </div>
                    <div className="space-y-2">
                      {enrichment.relatedTools.map((tool, i) => (
                        <Link
                          key={i}
                          href={tool.url}
                          className="block p-3 rounded-lg bg-gray-50 hover:bg-[#ff9951]/5 transition-colors group"
                        >
                          <div className="font-medium text-sm text-gray-900 group-hover:text-[#ff9951] flex items-center gap-1">
                            {tool.name}
                            <ArrowRightIcon className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5 capitalize">{tool.benefit}</div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* RELATED COLLECTIONS */}
                {enrichment?.relatedCollections && enrichment.relatedCollections.length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-xl p-5">
                    <span className="font-mono text-[11px] font-bold tracking-[0.15em] text-[#ff9951] block mb-3">
                      SHOP RELATED
                    </span>
                    <div className="flex items-center gap-2 mb-4">
                      <ShoppingBagIcon className="w-5 h-5 text-[#0469f0]" />
                      <h3 className="font-bold text-gray-900">Collections</h3>
                    </div>
                    <div className="space-y-2">
                      {enrichment.relatedCollections.map((collection, i) => (
                        <Link
                          key={i}
                          href={collection.url}
                          className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-[#ff9951]/5 transition-colors group"
                        >
                          <span className="font-medium text-sm text-gray-900 group-hover:text-[#ff9951]">
                            {collection.name}
                          </span>
                          <ArrowRightIcon className="w-3.5 h-3.5 text-gray-400 group-hover:text-[#ff9951]" />
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* ABOUT FRIDGE.COM — desktop sidebar instance. On narrow
                    screens this whole aside stacks BELOW everything, which
                    buried the identity block thousands of pixels deep
                    (FRIDGE-609); the lg:hidden twin right after the release
                    body is what phones see instead. Sticky, so the long
                    release bodies never scroll beside a dead-empty rail
                    (owner, 08/29/2026). */}
                <div className="hidden lg:block lg:sticky lg:top-24">
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                  {/* FRIDGE-609 step-up: an orange accent band anchors the block
                      as the page's identity card instead of a gray sidebar tile. */}
                  <div className="h-1 bg-[#ff9951]" aria-hidden="true" />
                  <div className="p-5">
                  <span className="font-mono text-[11px] font-bold tracking-[0.15em] text-[#ff9951] block mb-3">
                    ABOUT FRIDGE.COM
                  </span>
                  <div className="flex items-center gap-2 mb-4">
                    <GlobeIcon className="w-5 h-5 text-[#0469f0]" />
                    <h3 className="font-black text-gray-900 text-[15px]">The refrigerator and freezer search engine</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                    The authoritative refrigerator and freezer search engine in the United States.
                  </p>
                  {/* The owner's four ruled trust lines (lib/trust-signals.ts) —
                      the same set the footer and Why-trust band render. */}
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    {TRUST_SIGNALS.map((signal) => (
                      <div key={signal.key} className="text-center p-2 bg-gray-50 rounded-lg">
                        <div className="text-sm font-bold text-gray-900">{signal.value}</div>
                        <div className="text-xs text-gray-500 leading-snug">{signal.label}</div>
                      </div>
                    ))}
                  </div>
                  <div className="space-y-2">
                    <Link href="/tools" className="flex items-center gap-2 text-sm text-[#0469f0] hover:underline">
                      <Wrench className="w-3.5 h-3.5" /> Explore free tools
                    </Link>
                    <Link href="/collections/refrigerators" className="flex items-center gap-2 text-sm text-[#0469f0] hover:underline">
                      <ShoppingBagIcon className="w-3.5 h-3.5" /> Shop refrigerators
                    </Link>
                    <Link href="/blogs/news" className="flex items-center gap-2 text-sm text-[#0469f0] hover:underline">
                      <BookOpen className="w-3.5 h-3.5" /> Read expert articles
                    </Link>
                  </div>
                  </div>
                </div>
                </div>

                {/* PRESS RESOURCES */}
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                  <span className="font-mono text-[11px] font-bold tracking-[0.15em] text-[#ff9951] block mb-3">
                    PRESS RESOURCES
                  </span>
                  <div className="flex items-center gap-2 mb-4">
                    <Newspaper className="w-5 h-5 text-[#0469f0]" />
                    <h3 className="font-bold text-gray-900">Media</h3>
                  </div>
                  <div className="space-y-2">
                    <a href="/press/feed.xml" className="flex items-center gap-2 text-sm text-[#0469f0] hover:underline">
                      <Rss className="w-3.5 h-3.5" /> RSS feed
                    </a>
                    <Link href="/press" className="flex items-center gap-2 text-sm text-[#0469f0] hover:underline">
                      <Newspaper className="w-3.5 h-3.5" /> All press releases
                    </Link>
                    <Link href="/contact" className="flex items-center gap-2 text-sm text-[#0469f0] hover:underline">
                      <Users className="w-3.5 h-3.5" /> Media inquiries
                    </Link>
                    <Link href="/about-us" className="flex items-center gap-2 text-sm text-[#0469f0] hover:underline">
                      <GlobeIcon className="w-3.5 h-3.5" /> About us
                    </Link>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>

        {/* PREV / NEXT */}
        {(adjacent.prev || adjacent.next) && (
          <section style={{ borderBottom: '1px solid #f3f4f6', background: 'var(--gray-50)' }}>
            <div className="ed-container" style={{ paddingTop: 24, paddingBottom: 24 }}>
              <div className="flex items-center justify-between gap-4">
                {adjacent.prev ? (
                  <Link
                    href={`/press/${adjacent.prev.slug}`}
                    className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#ff9951] transition-colors max-w-[45%]"
                  >
                    <ChevronLeftIcon className="w-4 h-4 shrink-0" />
                    <span className="truncate">{adjacent.prev.title}</span>
                  </Link>
                ) : <div />}
                {adjacent.next ? (
                  <Link
                    href={`/press/${adjacent.next.slug}`}
                    className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#ff9951] transition-colors max-w-[45%] text-right"
                  >
                    <span className="truncate">{adjacent.next.title}</span>
                    <ChevronRightIcon className="w-4 h-4 shrink-0" />
                  </Link>
                ) : <div />}
              </div>
            </div>
          </section>
        )}

        {/* RELATED RELEASES */}
        {relatedReleases.length > 0 && (
          <section style={{ borderBottom: '1px solid #f3f4f6' }}>
            <div className="ed-container" style={{ paddingTop: 48, paddingBottom: 40 }}>
              <div className="ed-section-rule">
                <h2 style={{ fontSize: 26 }}>More research from Fridge.com</h2>
                <span className="meta">{relatedReleases.length} related · most recent first</span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {relatedReleases.slice(0, 3).map((pr) => (
                  <PressArticleCard key={pr.slug} release={pr} />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA BAND */}
        <CTABand
          eyebrow="Media inquiries"
          title="Fridge.com research is available for citation"
          body="For press inquiries, data requests, or partnership opportunities, contact our team or subscribe to the RSS feed."
          buttonLabel="Contact Fridge.com"
          buttonHref="/contact"
        />
      </main>

      <SiteFooter />
    </div>
  )
}
